import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AppAutocomplete, AppColorField, AppTextField } from "../form";
import { AppFormField } from "../layout";
import { applyOptionFieldChange, type WidgetChartMapping } from "../../utils/chartMappingBuilder";
import type { ChartOptionFieldDef } from "../../utils/chartOptionRegistry";
import { getOptionPath } from "../../utils/chartOptionPaths";
import { chartOptionOutlinedInputSx } from "../../lib/ui/formLayout";
import ChartOptionFieldShell from "./ChartOptionFieldShell";
import ChartOptionSwitchField from "./ChartOptionSwitchField";
import { getCompactFieldSize } from "./chartOptionLayout";

type Props = {
  field: ChartOptionFieldDef;
  mapping: WidgetChartMapping;
  disabled?: boolean;
  onChange: (next: WidgetChartMapping) => void;
};

type SelectOption = { value: string; label: string };

function formatFieldValue(value: unknown, field: ChartOptionFieldDef): string {
  if (value === undefined || value === null) return "";
  if (field.type === "boolean") return value ? "true" : "false";
  if (Array.isArray(value)) return JSON.stringify(value);
  return String(value);
}

function parseFieldValue(raw: string, field: ChartOptionFieldDef): unknown {
  if (field.type === "number") {
    if (raw.trim() === "") return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }
  if (field.type === "boolean") return raw === "true";
  return raw;
}

export default function ChartOptionCompactField({ field, mapping, disabled, onChange }: Props) {
  const { t } = useTranslation();
  const value = getOptionPath(mapping.optionTemplate, field.path);
  const label = t(field.labelKey);
  const helper = field.hintKey ? t(field.hintKey) : undefined;
  const size = getCompactFieldSize(field);

  const selectOptions = useMemo<SelectOption[]>(
    () =>
      (field.options ?? []).map((opt) => ({
        value: String(opt.value),
        label: t(opt.labelKey),
      })),
    [field.options, t]
  );

  const setValue = (nextValue: unknown) => {
    onChange(applyOptionFieldChange(mapping, field.path, nextValue));
  };

  let control = null;

  if (field.type === "boolean") {
    control = (
      <ChartOptionSwitchField
        label={label}
        checked={Boolean(value)}
        disabled={disabled}
        helperText={helper}
        onChange={(_, checked) => setValue(checked)}
      />
    );
  } else if (field.type === "select" && field.options) {
    control = (
      <ChartOptionFieldShell label={label} helperText={helper} disabled={disabled}>
        <AppAutocomplete<SelectOption>
          label={label}
          options={selectOptions}
          value={selectOptions.find((o) => o.value === String(value ?? "")) ?? null}
          disabled={disabled}
          onChange={(_, opt) => setValue(opt?.value ?? "")}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(a, b) => a.value === b.value}
          disableClearable
          renderInput={(params) => (
            <AppTextField
              {...params}
              hiddenLabel
              label={label}
              margin="none"
              sx={chartOptionOutlinedInputSx}
            />
          )}
          sx={chartOptionOutlinedInputSx}
        />
      </ChartOptionFieldShell>
    );
  } else if (field.type === "color") {
    control = (
      <AppColorField
        compact
        label={label}
        value={formatFieldValue(value, field)}
        disabled={disabled}
        onChange={(next) => setValue(next)}
        helperText={helper}
        pickColorLabel={t("chartOptions.colorPicker.pick")}
      />
    );
  } else {
    control = (
      <ChartOptionFieldShell label={label} helperText={helper} disabled={disabled}>
        <AppTextField
          hiddenLabel
          label={label}
          margin="none"
          type={field.type === "number" ? "number" : "text"}
          value={formatFieldValue(value, field)}
          disabled={disabled}
          onChange={(e) => setValue(parseFieldValue(e.target.value, field))}
          sx={chartOptionOutlinedInputSx}
          inputProps={{
            min: field.min,
            max: field.max,
            step: field.step,
          }}
        />
      </ChartOptionFieldShell>
    );
  }

  return (
    <AppFormField size={size} align="stretch">
      {control}
    </AppFormField>
  );
}
